import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Shield, Users, Building2, TrendingUp, Sparkles, Activity, KeyRound, LogOut, X, UserPlus, Ban, CheckCircle2, Gift } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
const btn = "inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground";
const primary = "inline-flex items-center gap-1.5 rounded-full brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60";
const danger = "inline-flex items-center gap-1.5 rounded-full border border-brand-red text-brand-red px-3 py-1.5 text-xs font-semibold hover:bg-brand-red/10";

const inputCls = "w-full rounded-xl border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";

function StatCard({ label, value, hint, testid }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4" data-testid={testid}>
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="mt-1 font-display text-2xl font-semibold">{value}</p>
      {hint && <p className="mt-1 text-[11px] text-muted-foreground">{hint}</p>}
    </div>
  );
}

function GrantDrawer({ org, onClose, onGranted }) {
  const [form, setForm] = useState({
    plan: "growth", source: "SERVICE_CLIENT",
    expires_at: "", reference: "", reason: "", notify: true,
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/superadmin/entitlements/grant", { org_id: org.org_id, ...form });
      toast.success("Complimentary access granted.");
      onGranted && onGranted();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Grant failed.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" data-testid="sa-grant-drawer">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-card p-5 sm:rounded-3xl sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="eyebrow text-brand-teal">Grant complimentary access</p>
            <h3 className="mt-1 font-display text-lg font-semibold tracking-tight">{org.name}</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="sa-grant-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <label className="block text-xs font-medium text-muted-foreground">Plan
            <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className={`${inputCls} mt-1`} data-testid="sa-grant-plan">
              <option value="growth">Growth</option>
              <option value="business">Business</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">Source
            <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={`${inputCls} mt-1`} data-testid="sa-grant-source">
              <option value="SERVICE_CLIENT">Service Client</option>
              <option value="HUB_CUSTOMER">Hub Customer</option>
              <option value="PARTNER">Partner</option>
              <option value="ADMIN_GRANT">Admin Grant</option>
              <option value="ENTERPRISE">Enterprise</option>
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">Expires at (optional)
            <input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} className={`${inputCls} mt-1`} data-testid="sa-grant-expires" />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">Reference (optional)
            <input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} className={`${inputCls} mt-1`} placeholder="e.g. invoice number or partner ID" data-testid="sa-grant-reference" />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">Reason (optional)
            <textarea rows={2} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className={`${inputCls} mt-1 resize-none`} data-testid="sa-grant-reason" />
          </label>
          <label className="flex items-center gap-2 pt-2 text-xs" data-testid="sa-grant-notify-row">
            <input type="checkbox" checked={form.notify} onChange={(e) => setForm({ ...form, notify: e.target.checked })} className="h-4 w-4 rounded border-border" data-testid="sa-grant-notify" />
            Notify customer by email
          </label>
        </div>
        <div className="mt-5 flex gap-2">
          <button onClick={onClose} className="flex-1 rounded-full border border-border px-4 py-2 text-sm font-semibold">Cancel</button>
          <button onClick={submit} disabled={busy} className={`${primary} flex-1`} data-testid="sa-grant-submit">{busy ? "Granting…" : "Grant access"}</button>
        </div>
      </div>
    </div>
  );
}

function FoundingApproveDrawer({ req, onClose, onApproved }) {
  const defaultExpiry = new Date();
  defaultExpiry.setFullYear(defaultExpiry.getFullYear() + 1);
  const defaultIso = defaultExpiry.toISOString().slice(0, 10);
  const [plan, setPlan] = useState(req.requested_plan || "growth");
  const [expiresAt, setExpiresAt] = useState(defaultIso);
  const [reason, setReason] = useState("Workspace Launch Offer complimentary access");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/superadmin/founding/requests/${req.request_id}/approve`, { plan, expires_at: expiresAt, reason });
      toast.success("Launch Offer approved.");
      onApproved && onApproved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Approval failed.");
    } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4" data-testid="sa-founding-approve-drawer">
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-t-3xl bg-card p-5 sm:rounded-3xl sm:p-8" onClick={(e)=>e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <p className="eyebrow text-brand-teal">Approve Launch Offer</p>
            <h3 className="mt-1 font-display text-lg font-semibold tracking-tight">{req.email}</h3>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="sa-founding-approve-close"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-3">
          <label className="block text-xs font-medium text-muted-foreground">Plan
            <select value={plan} onChange={(e)=>setPlan(e.target.value)} className={`${inputCls} mt-1`} data-testid="sa-founding-approve-plan">
              <option value="growth">Growth</option>
              <option value="business">Business</option>
            </select>
          </label>
          <label className="block text-xs font-medium text-muted-foreground">Complimentary access until
            <input type="date" value={expiresAt} onChange={(e)=>setExpiresAt(e.target.value)} className={`${inputCls} mt-1`} data-testid="sa-founding-approve-expires" />
          </label>
          <label className="block text-xs font-medium text-muted-foreground">Internal reason
            <input value={reason} onChange={(e)=>setReason(e.target.value)} className={`${inputCls} mt-1`} data-testid="sa-founding-approve-reason" />
          </label>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className={btn}>Cancel</button>
          <button onClick={submit} disabled={busy} className={primary} data-testid="sa-founding-approve-submit">
            {busy ? "Approving…" : "Approve & activate"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminPortal() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("dashboard");
  const [authorized, setAuthorized] = useState(null);
  const [me, setMe] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [userQuery, setUserQuery] = useState("");
  const [orgs, setOrgs] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [audit, setAudit] = useState([]);
  const [grantOrg, setGrantOrg] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [foundingRequests, setFoundingRequests] = useState([]);
  const [approvingReq, setApprovingReq] = useState(null);

  useEffect(() => {
    api.get("/superadmin/me")
      .then((r) => { setAuthorized(true); setMe(r.data); })
      .catch(() => { setAuthorized(false); });
  }, []);

  const loadDashboard = useCallback(async () => {
    try { const { data } = await api.get("/superadmin/dashboard"); setDashboard(data); } catch { /* silent */ }
  }, []);
  const loadUsers = useCallback(async () => {
    try {
      const { data } = await api.get(`/superadmin/users${userQuery ? `?q=${encodeURIComponent(userQuery)}` : ""}`);
      setUsers(data.users || []);
    } catch { /* silent */ }
  }, [userQuery]);
  const loadOrgs = useCallback(async () => {
    try { const { data } = await api.get("/superadmin/organizations"); setOrgs(data.organizations || []); } catch { /* silent */ }
  }, []);
  const loadAdmins = useCallback(async () => {
    try { const { data } = await api.get("/superadmin/administrators"); setAdmins(data.administrators || []); } catch { /* silent */ }
  }, []);
  const loadAudit = useCallback(async () => {
    try { const { data } = await api.get("/superadmin/audit-log"); setAudit(data.logs || []); } catch { /* silent */ }
  }, []);
  const loadFoundingRequests = useCallback(async () => {
    try { const { data } = await api.get("/superadmin/founding/requests"); setFoundingRequests(data.requests || []); } catch { /* silent */ }
  }, []);

  useEffect(() => { if (!authorized) return;
    if (tab === "dashboard") loadDashboard();
    if (tab === "users") loadUsers();
    if (tab === "orgs") loadOrgs();
    if (tab === "admins") loadAdmins();
    if (tab === "audit") loadAudit();
    if (tab === "founding") loadFoundingRequests();
  }, [tab, authorized, loadDashboard, loadUsers, loadOrgs, loadAdmins, loadAudit, loadFoundingRequests]);

  if (authorized === null) {
    return <div className="grid min-h-screen place-items-center bg-background text-sm text-muted-foreground">Checking authorization…</div>;
  }
  if (!authorized) {
    // Never reveal that /superadmin exists — mimic Not Found
    return (
      <div className="grid min-h-screen place-items-center bg-background p-6 text-center" data-testid="superadmin-not-found">
        <div>
          <p className="font-display text-6xl font-semibold text-muted-foreground/40">404</p>
          <p className="mt-2 text-sm text-muted-foreground">The page you're looking for doesn't exist.</p>
          <button onClick={() => navigate("/")} className={`${btn} mt-6`}>Back to home</button>
        </div>
      </div>
    );
  }

  const suspendUser = async (u) => {
    if (!window.confirm(`Suspend ${u.email}?`)) return;
    try { await api.patch(`/superadmin/users/${u.user_id}/status`, { status: "suspended" }); toast.success("User suspended."); loadUsers(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  };
  const reactivateUser = async (u) => {
    try { await api.patch(`/superadmin/users/${u.user_id}/status`, { status: "active" }); toast.success("User reactivated."); loadUsers(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  };
  const inviteAdmin = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/superadmin/administrators/invite", { email: inviteEmail });
      toast.success(data.auto_activated ? "Administrator activated." : "Invitation logged. Account will auto-activate on verification.");
      setInviteEmail("");
      loadAdmins();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  };
  const revokeAdmin = async (a) => {
    if (!window.confirm(`Revoke Super Admin from ${a.email}?`)) return;
    try { await api.post(`/superadmin/administrators/${a.user_id}/action`, { action: "revoke" }); toast.success("Revoked."); loadAdmins(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
  };

  return (
    <div className="min-h-screen bg-background" data-testid="superadmin-portal">
      <header className="sticky top-0 z-30 flex items-center justify-between border-b border-border bg-card/80 px-4 py-3 backdrop-blur sm:px-6">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg brand-gradient-bg text-white"><Shield className="h-4 w-4" /></span>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">StUpEco · Super Admin</p>
            <p className="text-sm font-semibold">{me?.email} {me?.protected && <span className="ml-1 rounded bg-brand-teal/20 px-1.5 py-0.5 text-[9px] font-bold uppercase text-brand-teal">Protected</span>}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => navigate("/app")} className={btn} data-testid="sa-back-workspace">Back to Workspace</button>
          <button onClick={() => { logout(); navigate("/"); }} className={btn} data-testid="sa-logout"><LogOut className="h-3.5 w-3.5" /> Log out</button>
        </div>
      </header>

      <nav className="flex gap-1 overflow-x-auto border-b border-border bg-background/60 px-3 py-2 sm:px-6" data-testid="sa-nav">
        {[["dashboard","Dashboard",Activity],["founding","Launch Offer Requests",Gift],["users","Users",Users],["orgs","Organizations",Building2],
          ["admins","Administrators",KeyRound],["audit","Audit log",Shield]].map(([k,l,Icon])=>(
          <button key={k} onClick={()=>setTab(k)} data-testid={`sa-tab-${k}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold ${tab===k?"bg-foreground text-background":"text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-3.5 w-3.5" /> {l}
          </button>
        ))}
      </nav>

      <main className="mx-auto max-w-6xl px-4 py-6 sm:px-6 sm:py-8">
        {tab === "dashboard" && dashboard && (
          <div className="space-y-5" data-testid="sa-dashboard">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard testid="sa-stat-users" label="Total users" value={dashboard.total_users} hint={`+${dashboard.new_registrations_7d} last 7d`} />
              <StatCard testid="sa-stat-active" label="Active users (7d)" value={dashboard.active_users_7d} />
              <StatCard testid="sa-stat-orgs" label="Organizations" value={dashboard.total_organizations} />
              <StatCard testid="sa-stat-paying" label="Paying subscriptions" value={dashboard.paying_subscriptions} hint={`${dashboard.complimentary_active} complimentary`} />
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-border bg-card p-5">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Plan distribution</p>
                <ul className="mt-2 space-y-1 text-sm">
                  {Object.entries(dashboard.plan_distribution || {}).map(([plan, n]) => (
                    <li key={plan} className="flex justify-between"><span className="capitalize">{plan}</span><span className="font-semibold">{n}</span></li>
                  ))}
                </ul>
              </div>
              <div className="rounded-2xl border border-brand-teal/40 bg-brand-teal/5 p-5" data-testid="sa-founding-card">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-brand-teal">Workspace Launch Offer campaign</p>
                <p className="mt-2 font-display text-2xl font-semibold">{dashboard.founding.spots_taken} / {dashboard.founding.max_spots} claimed</p>
                <p className="mt-1 text-xs text-muted-foreground">{dashboard.founding.remaining} spots remaining · ends {dashboard.founding.deadline.slice(0,10)}</p>
              </div>
            </div>
            <div className="rounded-2xl border border-border bg-card p-5">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Recent registrations</p>
              <ul className="mt-3 divide-y divide-border">
                {(dashboard.recent_registrations || []).map((u) => (
                  <li key={u.user_id} className="flex items-center gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1 truncate">{u.name} <span className="text-muted-foreground">· {u.email}</span></span>
                    <span className="text-[11px] text-muted-foreground">{(u.created_at||"").slice(0,10)}</span>
                    {u.email_verified && <CheckCircle2 className="h-3.5 w-3.5 text-brand-teal" />}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div className="space-y-3" data-testid="sa-users">
            <div className="flex items-center gap-2">
              <input value={userQuery} onChange={(e)=>setUserQuery(e.target.value)} onKeyDown={(e)=>e.key==="Enter"&&loadUsers()} placeholder="Search email or name…" className={inputCls} data-testid="sa-users-search" />
              <button onClick={loadUsers} className={btn} data-testid="sa-users-refresh">Search</button>
            </div>
            <div className="overflow-hidden rounded-2xl border border-border bg-card">
              <table className="w-full text-left text-xs" data-testid="sa-users-table">
                <thead className="bg-muted/50">
                  <tr>{["Email","Name","Plan","Verified","Status","Registered","Actions"].map(h=><th key={h} className="px-3 py-2 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {users.map((u)=>(
                    <tr key={u.user_id} className="border-t border-border" data-testid={`sa-user-row-${u.user_id}`}>
                      <td className="px-3 py-2">{u.email}</td>
                      <td className="px-3 py-2">{u.name}</td>
                      <td className="px-3 py-2 capitalize">{(u.org||{}).plan || "—"}</td>
                      <td className="px-3 py-2">{u.email_verified ? "Yes" : "No"}</td>
                      <td className="px-3 py-2 capitalize">{u.status || "active"}</td>
                      <td className="px-3 py-2 text-muted-foreground">{(u.created_at||"").slice(0,10)}</td>
                      <td className="px-3 py-2">
                        {u.status === "suspended" ? (
                          <button onClick={()=>reactivateUser(u)} className={btn} data-testid={`sa-user-reactivate-${u.user_id}`}><CheckCircle2 className="h-3 w-3" /> Reactivate</button>
                        ) : (
                          <button onClick={()=>suspendUser(u)} className={danger} data-testid={`sa-user-suspend-${u.user_id}`}><Ban className="h-3 w-3" /> Suspend</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">No users match this search.</p>}
            </div>
          </div>
        )}

        {tab === "orgs" && (
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3" data-testid="sa-orgs">
            {orgs.map((o)=>(
              <div key={o.org_id} className="rounded-2xl border border-border bg-card p-4" data-testid={`sa-org-${o.org_id}`}>
                <div className="flex items-baseline justify-between">
                  <h3 className="truncate font-display text-sm font-semibold">{o.name}</h3>
                  <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase text-muted-foreground">{o.plan || "free"}</span>
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">{o.members_count} members · {o.plan_source || "no source"} {o.plan_expires_at && `· expires ${o.plan_expires_at.slice(0,10)}`}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={()=>setGrantOrg(o)} className={primary + " text-xs"} data-testid={`sa-org-grant-${o.org_id}`}>
                    <Sparkles className="h-3 w-3" /> Grant access
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "admins" && (
          <div className="space-y-4" data-testid="sa-admins">
            <form onSubmit={inviteAdmin} className="flex gap-2" data-testid="sa-admin-invite-form">
              <input type="email" required value={inviteEmail} onChange={(e)=>setInviteEmail(e.target.value)} placeholder="new.admin@stupeco.com" className={inputCls} data-testid="sa-admin-invite-email" />
              <button type="submit" className={primary} data-testid="sa-admin-invite-btn"><UserPlus className="h-4 w-4" /> Invite</button>
            </form>
            <div className="rounded-2xl border border-border bg-card">
              <ul className="divide-y divide-border">
                {admins.map((a)=>(
                  <li key={a.user_id} className="flex items-center gap-3 p-3" data-testid={`sa-admin-${a.user_id}`}>
                    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg brand-gradient-bg text-white"><Shield className="h-4 w-4" /></span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{a.email}</p>
                      <p className="text-[11px] text-muted-foreground">{a.name} · {a.platform_role_source || "bootstrap"}</p>
                    </div>
                    {a.protected ? (
                      <span className="rounded-full bg-brand-teal/20 px-2 py-0.5 text-[10px] font-bold text-brand-teal">Protected</span>
                    ) : (
                      <button onClick={()=>revokeAdmin(a)} className={danger} data-testid={`sa-admin-revoke-${a.user_id}`}>Revoke</button>
                    )}
                  </li>
                ))}
              </ul>
              {admins.length === 0 && <p className="p-4 text-center text-sm text-muted-foreground">No administrators yet.</p>}
            </div>
          </div>
        )}

        {tab === "audit" && (
          <div className="rounded-2xl border border-border bg-card" data-testid="sa-audit">
            <ul className="max-h-[70vh] divide-y divide-border overflow-y-auto text-xs">
              {audit.map((a, i)=>(
                <li key={i} className="grid grid-cols-[100px_180px_1fr_120px] gap-2 p-2.5">
                  <span className="rounded bg-muted px-1.5 py-0.5 font-mono text-[10px]">{a.event}</span>
                  <span className="truncate text-muted-foreground">{a.actor_email}</span>
                  <span className="truncate">{a.target_email || (a.meta && JSON.stringify(a.meta)) || ""}</span>
                  <span className="text-right text-[10px] text-muted-foreground">{(a.created_at||"").replace("T"," ").slice(0,16)}</span>
                </li>
              ))}
              {audit.length === 0 && <li className="p-4 text-center text-sm text-muted-foreground">No audit entries yet.</li>}
            </ul>
          </div>
        )}

        {tab === "founding" && (
          <div className="rounded-2xl border border-border bg-card" data-testid="sa-founding">
            <div className="flex flex-wrap items-center gap-2 p-3">
              <p className="text-sm font-semibold">Launch Offer requests</p>
              <p className="ml-auto text-[11px] text-muted-foreground">{foundingRequests.length} total · {foundingRequests.filter(r=>r.status==='pending').length} pending</p>
            </div>
            <ul className="max-h-[70vh] divide-y divide-border overflow-y-auto text-xs">
              {foundingRequests.map((r) => (
                <li key={r.request_id} className="p-3" data-testid={`sa-founding-row-${r.request_id}`}>
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                      r.status === "pending" ? "bg-orange-500/10 text-brand-orange" :
                      r.status === "approved" ? "bg-brand-teal/10 text-brand-teal" :
                      "bg-muted text-muted-foreground"
                    }`}>{r.status}</span>
                    <p className="font-semibold text-sm">{r.name || r.email}</p>
                    <p className="text-muted-foreground">· {r.email}</p>
                    <p className="text-muted-foreground">· {r.business_name || "—"}</p>
                    <p className="text-muted-foreground">· {r.entity_type || "—"}</p>
                    <p className="text-muted-foreground">· {r.industry || "—"}{r.country ? `, ${r.country}` : ""}</p>
                    <p className="ml-auto text-muted-foreground">{(r.created_at || "").replace("T", " ").slice(0, 16)}</p>
                  </div>
                  <div className="mt-1 flex flex-wrap items-baseline gap-2">
                    <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide">Requested: {r.requested_plan}</span>
                    {r.approved_plan && <span className="rounded-full bg-brand-teal/20 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-teal">Approved: {r.approved_plan}</span>}
                    {r.approved_expires_at && <span className="text-[11px] text-muted-foreground">until {(r.approved_expires_at || "").slice(0, 10)}</span>}
                  </div>
                  {r.note && <p className="mt-1 rounded-lg bg-background/60 p-2 text-[11px] text-muted-foreground">Note: {r.note}</p>}
                  {r.status === "pending" && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      <button
                        className={primary}
                        onClick={() => setApprovingReq(r)}
                        data-testid={`sa-founding-approve-${r.request_id}`}
                      ><CheckCircle2 className="h-3.5 w-3.5" /> Approve</button>
                      <button
                        className={danger}
                        data-testid={`sa-founding-decline-${r.request_id}`}
                        onClick={async () => {
                          if (!window.confirm(`Decline Launch Offer request for ${r.email}?`)) return;
                          try {
                            await api.post(`/superadmin/founding/requests/${r.request_id}/decline`, { reason: "" });
                            toast.success("Declined.");
                            loadFoundingRequests();
                          } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed."); }
                        }}
                      ><Ban className="h-3.5 w-3.5" /> Decline</button>
                    </div>
                  )}
                </li>
              ))}
              {foundingRequests.length === 0 && <li className="p-4 text-center text-sm text-muted-foreground">No Launch Offer requests yet.</li>}
            </ul>
          </div>
        )}
      </main>

      {approvingReq && (
        <FoundingApproveDrawer
          req={approvingReq}
          onClose={() => setApprovingReq(null)}
          onApproved={() => { setApprovingReq(null); loadFoundingRequests(); }}
        />
      )}

      {grantOrg && <GrantDrawer org={grantOrg} onClose={()=>setGrantOrg(null)} onGranted={()=>loadOrgs()} />}
    </div>
  );
}
